package com.speedsoftware.nibbler.ui.boards

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.awaitLongPressOrCancellation
import androidx.compose.foundation.gestures.awaitTouchSlopOrCancellation
import androidx.compose.foundation.gestures.drag
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import kotlinx.coroutines.delay
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.requiredWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.composed
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.unit.toSize
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.zIndex
import com.speedsoftware.nibbler.models.TaskDto
import kotlin.math.roundToInt

val LocalKanbanDragDropState = compositionLocalOf<KanbanDragDropState> {
    error("No KanbanDragDropState provided")
}

class KanbanDragDropState {
    var draggingTask by mutableStateOf<TaskDto?>(null)

    /** Current pointer position in window coordinates. */
    var pointerInWindow by mutableStateOf(Offset.Zero)

    /** Where inside the card the pointer grabbed it, so the ghost stays under the finger/cursor. */
    var grabOffset by mutableStateOf(Offset.Zero)

    /** Size of the dragged card, fixed at drag start so the ghost never changes shape while it moves. */
    var dragCardSize by mutableStateOf(Size.Zero)
    var containerOriginInWindow by mutableStateOf(Offset.Zero)
    var hoveredStatus by mutableStateOf<String?>(null)
    var containerWidthPx by mutableStateOf(0f)

    val columnBounds = mutableStateMapOf<String, Rect>()
    val cardBounds = mutableStateMapOf<String, Rect>()

    /** Ids of the cards in each column, top to bottom, as currently displayed. */
    val columnTaskIds = mutableStateMapOf<String, List<String>>()

    /** Inside [hoveredStatus]: the card the dragged one would be put in front of; null means the end of the column. */
    var dropBeforeTaskId by mutableStateOf<String?>(null)

    fun registerColumnTasks(columnId: String, taskIds: List<String>) {
        if (columnTaskIds[columnId] != taskIds) columnTaskIds[columnId] = taskIds
    }

    fun unregisterCardBounds(taskId: String) {
        cardBounds.remove(taskId)
    }

    /** The slot under a pointer at height [pointerY]: before the first card whose centre is below it. */
    fun dropSlot(columnId: String, pointerY: Float): String? {
        val others = (columnTaskIds[columnId] ?: emptyList()).filter { it != draggingTask?.id }
        var lastVisible = -1
        for ((index, id) in others.withIndex()) {
            val bounds = cardBounds[id] ?: continue
            lastVisible = index
            if (pointerY < bounds.center.y) return id
        }
        // Below every card that is on screen: in front of the first one beyond them, or the end
        return others.getOrNull(lastVisible + 1)
    }

    /** False when dropping here would leave the card exactly where it already is. */
    fun dropWouldChange(columnId: String): Boolean {
        val task = draggingTask ?: return false
        val ids = columnTaskIds[columnId] ?: emptyList()
        val index = ids.indexOf(task.id)
        if (index < 0) return true // coming from another column
        return dropBeforeTaskId != ids.getOrNull(index + 1) && dropBeforeTaskId != task.id
    }

    fun registerColumnBounds(status: String, bounds: Rect) {
        columnBounds[status] = bounds
    }

    fun registerCardBounds(taskId: String, bounds: Rect) {
        cardBounds[taskId] = bounds
    }

    /** Top-left of the ghost card, relative to the drag container. */
    val ghostOriginInContainer: Offset
        get() = pointerInWindow - grabOffset - containerOriginInWindow

    fun onDragStart(task: TaskDto, pointerWindow: Offset) {
        draggingTask = task
        val bounds = cardBounds[task.id]
        grabOffset = pointerWindow - (bounds?.topLeft ?: pointerWindow)
        dragCardSize = bounds?.size ?: Size.Zero
        pointerInWindow = pointerWindow
        updateHoveredStatus(pointerWindow)
    }

    fun onDrag(pointerWindow: Offset) {
        pointerInWindow = pointerWindow
        updateHoveredStatus(pointerWindow)
    }

    /** Horizontal pointer position relative to the container, or null when nothing is dragged. */
    fun pointerXInContainer(): Float? =
        if (draggingTask == null) null else pointerInWindow.x - containerOriginInWindow.x

    fun refreshHover() {
        if (draggingTask != null) updateHoveredStatus(pointerInWindow)
    }

    private fun updateHoveredStatus(windowPos: Offset) {
        hoveredStatus = columnBounds.entries.firstOrNull { (_, rect) ->
            rect.contains(windowPos)
        }?.key
        dropBeforeTaskId = hoveredStatus?.let { dropSlot(it, windowPos.y) }
    }

    fun onDragEnd(onDrop: (TaskDto, String, String?) -> Unit) {
        val task = draggingTask
        val targetStatus = hoveredStatus
        if (task != null && targetStatus != null && dropWouldChange(targetStatus)) {
            onDrop(task, targetStatus, dropBeforeTaskId)
        }
        onDragCancel()
    }

    fun onDragCancel() {
        draggingTask = null
        hoveredStatus = null
        dropBeforeTaskId = null
    }
}

@Composable
fun KanbanDragDropContainer(
    state: KanbanDragDropState = remember { KanbanDragDropState() },
    scrollState: ScrollState? = null,
    content: @Composable () -> Unit
) {
    // Auto-scroll the board horizontally while a card is dragged close to its left/right edge
    LaunchedEffect(state.draggingTask != null, scrollState) {
        if (scrollState == null) return@LaunchedEffect
        while (state.draggingTask != null) {
            val x = state.pointerXInContainer()
            val width = state.containerWidthPx
            val edge = 80f
            if (x != null && width > 0f) {
                val delta = when {
                    x < edge -> -18f
                    x > width - edge -> 18f
                    else -> 0f
                }
                if (delta != 0f) {
                    scrollState.scrollBy(delta)
                    state.refreshHover()
                }
            }
            delay(16)
        }
    }

    CompositionLocalProvider(LocalKanbanDragDropState provides state) {
        Box(
            modifier = Modifier.onGloballyPositioned { coordinates ->
                val bounds = coordinates.boundsInWindow()
                state.containerOriginInWindow = bounds.topLeft
                state.containerWidthPx = bounds.width
            }
        ) {
            content()

            // Floating Ghost Card when dragging
            state.draggingTask?.let { task ->
                // The ghost keeps the exact size the card had when the drag began, even when the board
                // scrolls and the original card is pushed (partly) out of view
                val cardWidth = with(LocalDensity.current) { (state.dragCardSize.width.takeIf { it > 0f } ?: 280f).toDp() }

                Box(
                    modifier = Modifier
                        .requiredWidth(cardWidth)
                        .offset {
                            IntOffset(
                                state.ghostOriginInContainer.x.roundToInt(),
                                state.ghostOriginInContainer.y.roundToInt()
                            )
                        }
                        .zIndex(999f)
                        .graphicsLayer {
                            alpha = 0.92f
                            scaleX = 1.03f
                            scaleY = 1.03f
                            shadowElevation = 24f
                        }
                ) {
                    KanbanCard(
                        task = task,
                        onClick = {},
                        isGhost = true
                    )
                }
            }
        }
    }
}

fun Modifier.kanbanDropTarget(
    status: String,
    state: KanbanDragDropState
): Modifier = this.onGloballyPositioned { coordinates ->
    val bounds = coordinates.boundsInWindow()
    state.registerColumnBounds(status, bounds)
}

fun Modifier.kanbanCardBounds(
    taskId: String,
    state: KanbanDragDropState
): Modifier = composed {
    // A card that leaves the screen (lazy list) must not leave stale bounds behind
    DisposableEffect(taskId) { onDispose { state.unregisterCardBounds(taskId) } }
    this.onGloballyPositioned { coordinates ->
        // Unclipped position and size: boundsInWindow() would shrink to the visible part of a card that is
        // scrolled partly out of the board, which made the drag ghost narrower and taller
        state.registerCardBounds(taskId, Rect(coordinates.positionInWindow(), coordinates.size.toSize()))
    }
}

/**
 * Makes a card draggable. A mouse starts the drag as soon as it moves; touch needs a press-and-hold so
 * swiping the column still scrolls it. Pointer positions are converted to window coordinates, so the
 * drag stays correct wherever the card is. A plain click or tap is left alone for the card's click handler.
 */
fun Modifier.kanbanCardDrag(
    task: TaskDto,
    state: KanbanDragDropState,
    onDrop: (TaskDto, String, String?) -> Unit
): Modifier = composed {
    var coordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }
    fun toWindow(local: Offset): Offset = coordinates?.takeIf { it.isAttached }?.localToWindow(local) ?: local

    this
        .onGloballyPositioned { coordinates = it }
        .pointerInput(task.id) {
            awaitEachGesture {
                val down = awaitFirstDown(requireUnconsumed = false)
                val start: PointerInputChange? = if (down.type == PointerType.Mouse) {
                    var reached: PointerInputChange? = null
                    awaitTouchSlopOrCancellation(down.id) { change, _ -> change.consume(); reached = change }
                    reached
                } else {
                    awaitLongPressOrCancellation(down.id)
                }
                if (start != null) {
                    state.onDragStart(task, toWindow(start.position))
                    val finished = drag(start.id) { change ->
                        change.consume()
                        state.onDrag(toWindow(change.position))
                    }
                    if (finished) state.onDragEnd(onDrop) else state.onDragCancel()
                }
            }
        }
}
